import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Copy, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import type { Recipient } from '../../lib/recipients';

// ─────────────────────────────────────────────────────────────
// One email address on a ticket, shown in full and copyable.
//
// The sidebar is too narrow for "Name <address>" on one line, so
// the name and the address get their own lines and the address
// wraps rather than truncating — staff need to read the whole
// thing, not a prefix. Clicking anywhere on the row copies the
// bare address (no display name) so it pastes into a To: field.
// ─────────────────────────────────────────────────────────────

interface CopyableEmailProps {
  recipient: Recipient;
  /** Short chip on the left — "TO", "CC". */
  label: string;
  /** Tailwind classes for the chip, so TO and CC keep their colors. */
  labelClassName?: string;
  /** Optional trailing control, e.g. a CC remove button. */
  action?: ReactNode;
  className?: string;
}

export default function CopyableEmail({
  recipient,
  label,
  labelClassName = 'bg-primary-muted text-primary',
  action,
  className = '',
}: CopyableEmailProps) {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The row can unmount while the "copied" tick is still showing — a CC
  // removed right after being copied, say — so clear the pending reset.
  useEffect(() => () => {
    if (resetTimer.current) clearTimeout(resetTimer.current);
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(recipient.email);
      setCopied(true);
      toast.success('Email copied');
      if (resetTimer.current) clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      // Blocked clipboard (insecure origin, denied permission). The address
      // is fully visible above, so manual selection is a real fallback.
      toast.error('Could not access the clipboard — select the address and copy manually');
    }
  };

  return (
    <div className={`flex items-start gap-1.5 ${className}`}>
      <span className={`badge text-[10px] shrink-0 mt-0.5 ${labelClassName}`}>{label}</span>

      <button
        type="button"
        onClick={handleCopy}
        title={`Copy ${recipient.email}`}
        aria-label={`Copy email address ${recipient.email}`}
        className="group flex-1 min-w-0 text-left rounded px-1 -mx-1 py-0.5 hover:bg-row-even focus:outline-none focus-visible:ring-1 focus-visible:ring-primary transition-colors"
      >
        {recipient.name && <span className="block text-fg leading-snug">{recipient.name}</span>}
        <span className="flex items-start gap-1">
          <span className="break-all text-muted leading-snug">{recipient.email}</span>
          {copied ? (
            <Check className="w-3 h-3 shrink-0 mt-0.5 text-ok" aria-hidden="true" />
          ) : (
            <Copy
              className="w-3 h-3 shrink-0 mt-0.5 text-muted opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity"
              aria-hidden="true"
            />
          )}
        </span>
      </button>

      {action}
    </div>
  );
}
